/**
 * DepositCard — inline Square deposit collection on the confirm step.
 *
 * Shows the customer the amount, the slot-reservation expiry banner,
 * and the SquareCardForm. SquareCardForm tokenizes the card on
 * submit, then calls back with a `sourceId` nonce. The parent ships
 * that nonce to /api/appointments and the server charges + creates
 * the appointment atomically in the same request — no more
 * "skip pay later" loophole that left unpaid bookings lingering.
 *
 * Purely presentational — the parent owns the depositAmount state
 * (resolved by /api/appointments/deposit-check whenever the customer
 * picks a type) so the same value can drive the Confirm-button
 * suppression logic.
 *
 * Renders nothing when amount is null or zero.
 *
 * Extracted from consultation/page.tsx — Phase 1.4 of booking
 * consolidation (wiki/plans/booking-consolidation-2026-04-28.md).
 */
"use client";

import { CreditCard } from "lucide-react";
import { useTranslations } from "next-intl";
import { SquareCardForm } from "@/components/checkout/SquareCardForm";
import SlotReservationBanner from "@/components/booking/SlotReservationBanner";

interface SlotReservationLike {
  reservation: { expiresAt: string } | null;
  markExpired: () => void;
}

interface DepositCardProps {
  /** Dollar amount. Null while loading; 0 = no deposit; >0 = render. */
  amount: number | null;
  /**
   * Whether the parent's overall submit is in flight (e.g. waiting for
   * /api/appointments to come back). Disables the SquareCardForm so
   * the customer can't double-submit.
   */
  submitting: boolean;
  /**
   * Slot reservation hook return — used to render the 10-min expiry
   * banner inside the card so it's always visible while the customer
   * is staring at their card field. Pass the same `slotReservation`
   * the parent uses elsewhere on the page.
   */
  slotReservation: SlotReservationLike;
  /**
   * Called with the Square sourceId once tokenization succeeds. Parent
   * forwards this to its appointment-submit handler. Must return a
   * Promise — SquareCardForm awaits it before clearing its processing
   * state.
   */
  onPay: (sourceId: string) => Promise<void>;
  /** Toast hook from parent — error from SquareCardForm bubbles here. */
  onPaymentError: (msg: string) => void;
  /** SquareCardForm processing flag setter (parent owns the bool). */
  setPayingDeposit: (v: boolean) => void;
  payingDeposit: boolean;
  /**
   * i18n namespace. Defaults to "consultation". Phase 2 will rename.
   */
  i18nNamespace?: string;
}

export default function DepositCard({
  amount,
  submitting,
  slotReservation,
  onPay,
  onPaymentError,
  setPayingDeposit,
  payingDeposit,
  i18nNamespace = "consultation",
}: DepositCardProps) {
  const t = useTranslations(i18nNamespace);
  if (!amount || amount <= 0) return null;
  return (
    <div className="mt-8 rounded-xl border border-brand-gold/30 bg-brand-cream/30 p-5">
      <div className="flex items-center gap-3 pb-4 mb-4 border-b border-brand-gold/20">
        <CreditCard className="h-5 w-5 text-brand-gold" />
        <div>
          <p className="text-sm font-medium text-brand-dark">
            ${amount.toFixed(2)} Deposit Required
          </p>
          <p className="text-xs text-brand-muted mt-0.5">
            This deposit will be credited toward your wig purchase.
          </p>
        </div>
      </div>
      {slotReservation.reservation && (
        <div className="mb-3">
          <SlotReservationBanner
            expiresAt={slotReservation.reservation.expiresAt}
            onExpired={slotReservation.markExpired}
          />
        </div>
      )}
      <SquareCardForm
        amount={amount}
        isProcessing={payingDeposit || submitting}
        setIsProcessing={setPayingDeposit}
        onPaymentSuccess={(sourceId) => onPay(sourceId)}
        onPaymentError={onPaymentError}
        buttonLabel={`Pay $${amount.toFixed(2)} & Book Appointment`}
        processingLabel={t("booking")}
        walletLabel="Rivky Wigs Deposit"
      />
    </div>
  );
}
